import { ApolloClient } from '@apollo/client';
import { isLeasedCategory } from './common';
import { getItemPage } from '../../api/queries/item';
import {
  CurrentItemQueryVariables,
  FilteredAgenciesQueryVariables,
  ItemStatusEnum,
  ItemPageBaseQuery
} from '../../graphql/graphql';
import { cleanHtmlExtension } from './middleware';
import Bugsnag from '../bugsnag';

export const getFilterParamsToGetAgencyItems = (
  isLeased: boolean,
  agencyId: string,
  categoryId: string,
  activeCategoryId = 'all'
): FilteredAgenciesQueryVariables['filter'] => {
  const filterBy =
    activeCategoryId === 'all' || isLeased ? categoryId : activeCategoryId;

  if (isLeased) {
    return {
      agencyId,
      categoryId,
      leased: activeCategoryId === 'lease'
    };
  }

  if (isLeasedCategory(categoryId)) {
    return {
      agencyId,
      leased: categoryId === 'lease'
    };
  }

  return {
    agencyId,
    categoryId: filterBy
  };
};

export type CurrentItemData = ItemPageBaseQuery['item'] | null;
type ApolloNetworkError = { networkError?: { response?: { status?: number } } };

export const getResStatusCode = (item: CurrentItemData): number | null => {
  if (!item) return 404;

  const isExpired = item?.state === ItemStatusEnum.Expired;
  const isExpiredManually = !!(isExpired && item?.isExpiredManually);

  if (isExpired || isExpiredManually) return 404;

  return null;
};

export const getCurrentItemData = async (
  apolloClient: ApolloClient<unknown>,
  id: CurrentItemQueryVariables['id']
): Promise<{
  error: null | string;
  currentItemData: CurrentItemData;
  errorCode?: number | null;
  ssrVersion: string | null;
}> => {
  try {
    const { data } = await apolloClient.query<ItemPageBaseQuery>({
      query: getItemPage,
      variables: { id }
    });

    const currentItemData: CurrentItemData = data?.item || null;
    const ssrVersion = currentItemData?.updatedAt || null;

    return {
      currentItemData,
      ssrVersion,
      error: null
    };
  } catch (error: unknown) {
    const err = error as ApolloNetworkError;
    const errorCode = err?.networkError?.response?.status ?? null;
    Bugsnag.notify(
      new Error(`[SSR] Error in getCurrentItemData, status code: ${errorCode}`)
    );

    return {
      currentItemData: null,
      error: JSON.stringify(error),
      errorCode,
      ssrVersion: null
    };
  }
};

export function normalizeItemPagePath(rawUrl: string): string {
  const clearUrl = cleanHtmlExtension(rawUrl);
  const [pathWithoutQuery] = clearUrl.split('?');
  const withoutPrefix = pathWithoutQuery
    .replace(/^\/_next\/data\/[^/]+\/(az|ru)/, '')
    .replace(/^\/(az|ru)/, '');

  return withoutPrefix.replace(/\.json$/, '');
}

type GtagPayloadParams = {
  transactionId: string;
  value?: number | null;
  currency?: string;
  paymentType?: string;
  scenario?: string;
  isAuthor: boolean;
  adStatus?: ItemStatusEnum;
  items?: Array<{
    item_id: string;
    item_name: string;
    item_variant: string;
  }>;
  paymentMethod?: string;
  serviceType?: string;
  servicePrice?: number | null;
  failureReason?: string;
};

export const createPromotionGtagPayload = (
  params: GtagPayloadParams
): Record<string, unknown> => {
  const {
    transactionId,
    value,
    currency,
    paymentType,
    scenario,
    isAuthor,
    adStatus,
    items,
    paymentMethod,
    serviceType,
    servicePrice,
    failureReason
  } = params;

  const payload: Record<string, unknown> = {
    transaction_id: transactionId,
    scenario,
    user_type: isAuthor ? 'lister' : 'non_lister'
  };

  if (value !== undefined && value !== null) payload.value = value;
  if (currency) payload.currency = currency;
  if (paymentType) payload.payment_type = paymentType;
  if (adStatus) payload.ad_status = adStatus.toLowerCase();
  if (items) payload.items = items;

  if (paymentMethod) payload.payment_method = paymentMethod;
  if (serviceType) payload.service_type = serviceType;
  if (servicePrice !== undefined && value !== null)
    payload.service_price = servicePrice;
  if (failureReason) payload.failure_reason = failureReason;

  return payload;
};
